import { useState, useEffect } from "react";
import { MdHistory, MdClose } from "react-icons/md";
import { useTranslation } from "context/LanguageContext";
import { Trash2 } from "lucide-react";

const SEARCH_HISTORY_KEY = "search_history";
const MAX_HISTORY_ITEMS = 10;

export default function SearchHistory({ onSelectSearch, currentQuery }) {
  const [history, setHistory] = useState([]);
  const [showHistory, setShowHistory] = useState(false);
  const { t } = useTranslation();

  useEffect(() => {
    try {
      const savedHistory = localStorage.getItem(SEARCH_HISTORY_KEY);
      if (savedHistory) {
        setHistory(JSON.parse(savedHistory));
      }
    } catch (error) {
      console.error("Error loading search history:", error);
    }
  }, []);

  const saveToHistory = (searchQuery) => {
    if (!searchQuery || searchQuery.trim().length < 2) return;

    const trimmedQuery = searchQuery.trim();
    
    try {
      let updatedHistory = [...history];
      
      updatedHistory = updatedHistory.filter(
        (item) => item.toLowerCase() !== trimmedQuery.toLowerCase()
      );
      
      updatedHistory.unshift(trimmedQuery);
      
      updatedHistory = updatedHistory.slice(0, MAX_HISTORY_ITEMS);
      
      setHistory(updatedHistory);
      localStorage.setItem(SEARCH_HISTORY_KEY, JSON.stringify(updatedHistory));
    } catch (error) {
      console.error("Error saving search history:", error);
    }
  };

  const clearHistory = () => {
    setHistory([]);
    localStorage.removeItem(SEARCH_HISTORY_KEY);
  };

  const removeItem = (indexToRemove) => {
    try {
      const updatedHistory = history.filter((_, index) => index !== indexToRemove);
      setHistory(updatedHistory);
      localStorage.setItem(SEARCH_HISTORY_KEY, JSON.stringify(updatedHistory));
    } catch (error) {
      console.error("Error removing history item:", error);
    }
  };

  const handleHistoryClick = (searchTerm) => {
    onSelectSearch(searchTerm);
    setShowHistory(false);
  };

  useEffect(() => {
    if (!currentQuery && history.length > 0) {
      setShowHistory(true);
    } else {
      setShowHistory(false);
    }
  }, [currentQuery, history.length]);

  useEffect(() => {
    window.saveSearchHistory = saveToHistory;
  }, [history]);

  if (history.length === 0) return null;

  return (
    <div className="max-w-2xl mx-auto mb-6">
      {showHistory && (
        <div className="bg-white rounded-lg border border-gray-200 shadow-sm p-4">
          <div className="flex items-center justify-between mb-3">
            <div className="flex items-center gap-2 text-gray-600">
              <MdHistory className="w-5 h-5" />
              <span className="text-sm font-medium">{t("searchHistory")}</span>
            </div>
            <button
              onClick={clearHistory}
              className="text-xs text-red-500 hover:text-red-700 transition-colors"
            >
              <Trash2/>
            </button>
          </div>
          
          <div className="flex flex-wrap gap-2">
            {history.map((item, index) => (
              <div
                key={index}
                className="group flex items-center gap-1 px-3 py-1.5 bg-gray-100 hover:bg-gray-200 rounded-full text-sm text-gray-700 cursor-pointer transition-colors"
              >
                <span
                  onClick={() => handleHistoryClick(item)}
                  className="flex-1"
                >
                  {item}
                </span>
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    removeItem(index);
                  }}
                  className="opacity-0 group-hover:opacity-100 transition-opacity"
                  aria-label="Remove from history"
                >
                  <MdClose className="w-4 h-4 text-gray-500 hover:text-gray-700" />
                </button>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
